import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Save, X, Plus, Trash2, Settings, BarChart2,
    AlertTriangle, GripVertical, MoreVertical,
    ChevronRight, ChevronDown, Check, Type, Eye, EyeOff,
    Palette, Grid, Layers, Download, FileText, Image, ArrowLeft, LayoutGrid
} from 'lucide-react';
import { exportElementAsPNG, exportDataAsCSV } from '../utils/exportUtils';
import { ChartConfig, Dataset, DataRelationship, AdvancedConfig, WorkspaceProps } from '../types';
import { DEFAULT_PALETTE, COLOR_PALETTES, CHART_COLORS } from '../constants';
import { generateUUID } from '../utils/uuid';
import { ChartRenderer } from './ChartRenderer';
import ChartTypePicker from './ChartTypePicker';

import RelationshipGraph from './RelationshipGraph';
import { processDataForChart, processDataForAdvancedChart } from '../services/dataService';
import { BasicMappingZone } from './ChartBuilder/BasicMappingZone';
import { HeatmapMappingZone } from './ChartBuilder/HeatmapMappingZone';
import { EncodingBar } from './ChartBuilder/EncodingBar';
import { ChartTypeSettings } from './ChartBuilder/ChartTypeSettings';
import { StyleFormatSection } from './ChartBuilder/StyleFormatSection';
import { AdvancedSection } from './ChartBuilder/AdvancedSection';
import { HorizontalStyleBar } from './ChartBuilder/HorizontalStyleBar';
import { FieldPicker } from './ChartBuilder/FieldPicker';
import { AdvancedDataMapping } from './ChartBuilder/AdvancedDataMapping';
import { ScientificMappingZone } from './ChartBuilder/ScientificMappingZone';
import { KPIMappingZone } from './ChartBuilder/KPIMappingZone';


const defaultAdvancedConfig: AdvancedConfig = {
    scientific: {
        plotType: 'line',
        subplots: { rows: 1, cols: 1 },
        fieldMappings: {},
        axisOptions: {
            logScaleX: false,
            logScaleY: false,
            grid: true,
            minorTicks: false,
            minorGrid: false,
            axisFormat: 'plain'
        },
        analysis: {
            trendline: 'none',
            showEquation: false,
            slopeMode: 'overall',
            slopeRange: { min: 0, max: 100 }
        },
        errorBars: {
            show: false,
            mode: 'none',
            constantValue: 1,
            direction: 'symmetric',
            capSize: 5,
            lineThickness: 1
        },
        transformations: {
            x: 'none',
            y: 'none',
            normalize: false,
            standardize: false
        },
        style: {
            colormap: 'viridis',
            showColorbar: true,
            markerShape: 'circle',
            markerSize: 6,
            markerSizeMin: 2,
            markerSizeMax: 20,
            markerOutline: false,
            lineStyle: 'solid',
            lineWidth: 2,
            curveSmoothing: false,
            alpha: 0.8,
            showPointMarkers: true
        },
        legend: {
            show: true,
            position: 'right'
        },
        animation: {
            fadeIn: true,
            duration: 800
        },
        heatmap: {
            colorScale: 'sequential',
            resolution: 50,
            showContours: false,
            contourCount: 10,
            interpolation: true
        },
        threeD: {
            elevation: 30,
            azimuth: 45,
            projection: 'perspective',
            colorGradient: 'viridis',
            pointSize: 4,
            depthShading: false,
            showAxisBox: true,
            wireframeMode: false,
            meshDensity: 10
        },
        vector: {
            scale: 1,
            arrowSize: 1,
            normalize: false,
            autoGrid: false,
            magnitudeColoring: false
        }
    },
    vector: {
        uField: null,
        vField: null,
        arrowScaling: 1,
        normalize: false
    },
    threeD: {
        xField: null,
        yField: null,
        zField: null,
        pointSize: 4
    },
    map: {
        geoMode: 'points'
    },

};

const defaultChartConfig: ChartConfig = {
    id: 'new-chart',
    createdAt: new Date().toISOString(),
    datasetIds: [],
    type: 'bar',
    title: '',
    xField: '',
    yField: '', // Deprecated
    ySeries: [], // Deprecated
    measures: [],
    aggregation: 'count',
    advanced: defaultAdvancedConfig,
    customization: {
        xAxisLabel: '',
        yAxisLabel: '',
        color: '#4f46e5',
        showLegend: true,
        showDataLabels: false,
        showGrid: true,
        useMultipleColors: true,
        paletteName: 'vibrant',
        lineWidth: 2,
        areaOpacity: 0.8,
        roundedBars: false,
        pointSize: 6,
        pointOpacity: 0.9,
        labelContent: 'auto',
        showQuadrants: false,
    },
};

const validateChartConfig = (
    config: Partial<ChartConfig>,
    datasets: Dataset[],
    options: { isForSaving?: boolean } = {}
): string[] => {
    const { isForSaving = false } = options;
    const errors: string[] = [];
    if (isForSaving && !config.title) {
        errors.push("Chart Title is required to save.");
    }
    if (!config.datasetIds || config.datasetIds.length === 0) {
        errors.push("At least one dataset must be selected.");
    }

    // If no type is set, we can't validate further
    if (!config.type) {
        errors.push("Chart type must be selected.");
        return errors;
    }

    const numericCols = new Set<string>();
    const selectedDatasets = datasets.filter(ds => config.datasetIds?.includes(ds.id));
    selectedDatasets.forEach(ds => {
        ds.schema.forEach(col => {
            if (col.type === 'number') {
                // Match the logic used in ChartBuilder for field values
                if (selectedDatasets.length > 1) {
                    numericCols.add(`${ds.name}.${col.name}`);
                } else {
                    numericCols.add(col.name);
                }
            }
        });
    });

    switch (config.type) {
        case 'bar':
        case 'line':
        case 'area':
            if (!config.xField) errors.push("Missing X-Axis field. Please select a field to render the chart.");
            // Check if at least one visible measure exists
            const hasVisibleMeasure = config.measures?.some(m => m.visible);
            if (!hasVisibleMeasure && !config.yField && (!config.ySeries || config.ySeries.length === 0)) {
                errors.push("Missing Y-Series. Add at least one measure to generate a visualization.");
            }
            break;
        case 'pie':
            if (!config.xField) errors.push("A category field (X-Axis) is required.");
            if ((!config.measures || config.measures.length === 0) && !config.yField && (!config.ySeries || config.ySeries.length === 0)) errors.push("A numeric measure (Y-Axis) is required.");
            break;
        case 'scatter':
            if (!config.xField) {
                errors.push("A numeric X-Axis Field is required.");
            } else if (!numericCols.has(config.xField)) {
                errors.push("Scatter Plot X-Axis must be a numeric field.");
            }

            // Check Y-Axis (Measures)
            const hasNumericMeasure = config.measures?.some(m => {
                // Ideally we check if the measure field is numeric, but measures are usually numeric aggregations.
                // For scatter (raw), we check the underlying field.
                return numericCols.has(m.field);
            });

            if (!hasNumericMeasure && !config.yField && (!config.ySeries || config.ySeries.length === 0)) {
                errors.push("A numeric Y-Axis Field is required.");
            } else if (config.measures?.length === 1 && !numericCols.has(config.measures[0].field)) {
                errors.push("Scatter Plot Y-Axis must be a numeric field.");
            }
            break;
        case 'number':
            if (config.aggregation !== 'count' && (!config.measures || config.measures.length === 0) && !config.yField && (!config.ySeries || config.ySeries.length === 0)) {
                errors.push("A numeric Y-Axis Field is required unless using 'Count of Rows'.");
            }
            break;

        case 'map':
            const mapIso3 = config.iso3Field || config.advanced?.map?.regionKeyField;
            const mapCountry = config.countryField;
            const mapValue = config.valueField || config.advanced?.map?.valueField;
            const mapLat = config.latField || config.advanced?.map?.latField;
            const mapLon = config.lonField || config.advanced?.map?.lonField;
            // Map needs either region key OR lat/lon
            if (!mapIso3 && !mapCountry && (!mapLat || !mapLon)) {
                errors.push('ISO3 Code or Country Name field is required.');
            }
            if (!mapValue) errors.push('A numeric Value field is required.');
            break;
        case 'force-directed':
            if (!config.sourceField || !config.targetField) errors.push('Source and Target node fields are required.');
            break;
        case '3d-bar':
            if (!config.xField) errors.push('A category field (X-Axis) is required.');
            if (!config.yField) errors.push('A numeric measure (Y-Axis) is required.');
            break;
        case 'radar':
            if (!config.xField) errors.push("An X-Axis Field is required.");
            if (config.aggregation !== 'count' && (!config.measures || config.measures.length === 0) && !config.yField && (!config.ySeries || config.ySeries.length === 0)) {
                errors.push(`A numeric Y-Axis Field is required for '${config.aggregation}' aggregation.`);
            }
            break;
        case 'kpi-card':
            // KPI Card requires a value field
            if (!config.kpiValueField) {
                errors.push("A Value Field is required for KPI Card.");
            }
            break;
    }

    if (config.facets?.field) {
        // Optional: Add specific facet validation if needed
    }

    return errors;
};

const ChartBuilder: React.FC<Pick<WorkspaceProps, 'datasets' | 'relationships' | 'onCreateChart' | 'chartToEdit' | 'onUpdateChart' | 'onDeleteChart'> & { allCharts: ChartConfig[], onSelectChart: (id: string | null) => void }> = ({ datasets, relationships, onCreateChart, chartToEdit, onUpdateChart, onDeleteChart, allCharts, onSelectChart }) => {
    const [chartConfig, _setChartConfig] = useState<ChartConfig>(defaultChartConfig);
    const setChartConfig = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setChartConfig called with second argument (callback)! This causes React Error #310.', args);
            console.trace();
        }
        return _setChartConfig(args[0]);
    };

    const [showJoinedOnly, _setShowJoinedOnly] = useState(false);
    const setShowJoinedOnly = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setShowJoinedOnly called with second argument!', args);
            console.trace();
        }
        return _setShowJoinedOnly(args[0]);
    };

    const [previousYField, _setPreviousYField] = useState<string>('');
    const setPreviousYField = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setPreviousYField called with second argument!', args);
            console.trace();
        }
        return _setPreviousYField(args[0]);
    };

    const [dataError, _setDataError] = useState<string | null>(null);
    const setDataError = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setDataError called with second argument!', args);
            console.trace();
        }
        return _setDataError(args[0]);
    };

    const [showExportMenu, _setShowExportMenu] = useState(false);
    const setShowExportMenu = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setShowExportMenu called with second argument!', args);
            console.trace();
        }
        return _setShowExportMenu(args[0]);
    };

    const [isExporting, _setIsExporting] = useState(false);
    const setIsExporting = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setIsExporting called with second argument!', args);
            console.trace();
        }
        return _setIsExporting(args[0]);
    };

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const isEditing = !!chartToEdit;

    // Initialize config from props, but avoid overwriting local state on every render
    useEffect(() => {
        if (chartToEdit) {
            const newConfig = { ...defaultChartConfig, ...chartToEdit };

            // Migration: Convert legacy yField/ySeries to measures
            if (!newConfig.measures || newConfig.measures.length === 0) {
                const migratedMeasures: any[] = [];

                if (newConfig.ySeries && newConfig.ySeries.length > 0) {
                    newConfig.ySeries.forEach(s => {
                        migratedMeasures.push({
                            id: generateUUID(),
                            field: s.field,
                            aggregation: (s.aggregation || newConfig.aggregation || 'sum') as any,
                            colorMode: s.color === 'auto' ? 'auto' : 'fixed',
                            color: s.color === 'auto' ? undefined : s.color,
                            displayName: s.label || s.field,
                            isCustomLabel: !!s.label,
                            visible: s.visible !== false
                        });
                    });
                } else if (newConfig.yField) {
                    migratedMeasures.push({
                        id: generateUUID(),
                        field: newConfig.yField,
                        aggregation: (newConfig.aggregation || 'sum') as any,
                        colorMode: 'auto',
                        displayName: newConfig.yField,
                        isCustomLabel: false,
                        visible: true
                    });
                }

                if (migratedMeasures.length > 0) {
                    newConfig.measures = migratedMeasures;
                }
            }

            setChartConfig(newConfig);
        } else {
            setChartConfig(defaultChartConfig);
        }
    }, [chartToEdit]);



    const handleConfigChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (name === 'aggregation') {
            const newAggregation = value as any;
            if (value === 'count') {
                setPreviousYField(chartConfig.yField || '');
                setChartConfig(prev => ({
                    ...prev,
                    [name]: value,
                    yField: '',
                    // Update all measures to use 'count' if global aggregation changes to count
                    measures: prev.measures?.map(m => ({ ...m, aggregation: 'count' }))
                }));
            } else {
                // If switching from count, restore yField if needed, and update measures
                setChartConfig(prev => ({
                    ...prev,
                    [name]: newAggregation,
                    yField: prev.aggregation === 'count' ? previousYField : prev.yField,
                    // Update all measures to use the new aggregation
                    measures: prev.measures?.map(m => ({ ...m, aggregation: newAggregation }))
                }));
            }
        } else {
            setChartConfig(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
        }
    };

    const handleCustomizationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setChartConfig(prev => ({
            ...prev,
            customization: {
                ...prev.customization,
                [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
            }
        }));
    };

    const handleAdvancedConfigChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // This handler might be deprecated as AdvancedSettings handles its own updates via onChange prop
        // But keeping it for compatibility if needed, though AdvancedSettings uses a direct update callback.
    };

    const previewValidationErrors = validateChartConfig(chartConfig, datasets);
    const isPreviewValid = previewValidationErrors.length === 0;
    const saveValidationErrors = validateChartConfig(chartConfig, datasets, { isForSaving: true });
    const isSaveValid = saveValidationErrors.length === 0;

    const chartData = useMemo(() => {
        if (!isPreviewValid) return [];
        try {
            setDataError(null);
            if (['force-directed', '3d-bar', 'globe', 'map', 'scientific', 'vector', 'kpi-card'].includes(chartConfig.type || '')) {
                return processDataForAdvancedChart(datasets, relationships, chartConfig as ChartConfig);
            }
            return processDataForChart(datasets, relationships, chartConfig as ChartConfig);
        } catch (err) {
            console.error("Data processing error:", err);
            setDataError((err as Error).message);
            return [];
        }
    }, [chartConfig, datasets, relationships, isPreviewValid]);

    const [saveSuccess, _setSaveSuccess] = useState(false);
    const setSaveSuccess = (...args: any[]) => {
        if (args.length > 1) {
            console.error('setSaveSuccess called with second argument!', args);
            console.trace();
        }
        return _setSaveSuccess(args[0]);
    };

    const handleSaveChart = () => {
        if (!isSaveValid) {
            alert("Cannot save chart:\n" + saveValidationErrors.join("\n"));
            return;
        }
        if (isEditing && chartToEdit) {
            onUpdateChart({ ...chartToEdit, ...chartConfig } as ChartConfig);
        } else {
            onCreateChart(chartConfig as Omit<ChartConfig, 'id' | 'createdAt'>);
        }
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
    };

    const handleExport = async () => {
        if (!chartContainerRef.current) return;
        setIsExporting(true);
        setShowExportMenu(false);
        try {
            // Find the actual chart element to export (usually the ResponsiveContainer or the main chart div)
            // We'll target the div immediately inside the chartContainerRef that contains the chart
            // Export the entire container to include legends and other elements
            const elementToExport = chartContainerRef.current;

            await exportElementAsPNG(elementToExport, `chart-${chartConfig.title || 'export'}`);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleSelectionChange = (selection: any) => {
        console.log('Selection changed:', selection);
        // Future: Implement cross-filtering or other interactions
    };

    const selectedDatasets = datasets.filter(d => chartConfig.datasetIds?.includes(d.id));
    const allCols = selectedDatasets.flatMap(d => d.schema.map(c => {
        const label = selectedDatasets.length > 1 ? `${d.name}.${c.name}` : c.name;
        const value = selectedDatasets.length > 1 ? `${d.name}.${c.name}` : c.name;
        return { label, value, isNumeric: c.type === 'number', isConnection: false };
    }));

    // Identify joined fields - MUST come before using it
    const joinedFieldNames = useMemo(() => {
        if (selectedDatasets.length < 2) return [];
        const nameCounts = new Map<string, number>();
        selectedDatasets.forEach(ds => {
            ds.schema.forEach(col => {
                nameCounts.set(col.name, (nameCounts.get(col.name) || 0) + 1);
            });
        });
        return Array.from(nameCounts.entries()).filter(([_, count]) => count === selectedDatasets.length).map(([name]) => name);
    }, [selectedDatasets]);

    // Filter for joined fields if enabled
    const displayedCols = showJoinedOnly
        ? allCols.filter(c => joinedFieldNames.some(name => c.value.endsWith(`.${name}`)))
        : allCols;

    const numericCols = displayedCols.filter(c => c.isNumeric);

    // If no chart is selected, show the list view
    if (!chartToEdit) {
        return (
            <div className="p-8 h-full overflow-y-auto bg-transparent">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-3xl font-bold text-white tracking-tight">Charts</h2>
                        <p className="text-gray-400 mt-1">Manage and organize your visualizations</p>
                    </div>
                    <button
                        onClick={() => onCreateChart({ ...defaultChartConfig, title: 'New Chart' })}
                        className="flex items-center gap-2 px-4 py-2 bg-neon-gold/10 text-neon-gold border border-neon-gold/50 rounded-lg hover:bg-neon-gold hover:text-white transition-all shadow-glow"
                    >
                        <Plus className="w-4 h-4" /> New Chart
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {/* Create New Card */}
                    <button
                        onClick={() => onCreateChart({ ...defaultChartConfig, title: 'New Chart' })}
                        className="group flex flex-col items-center justify-center h-64 rounded-2xl border-2 border-dashed border-white/10 hover:border-neon-gold/50 bg-white/5 hover:bg-white/10 transition-all gap-4"
                    >
                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Plus className="w-8 h-8 text-gray-400 group-hover:text-neon-gold" />
                        </div>
                        <span className="text-gray-400 font-medium group-hover:text-white">Create New Chart</span>
                    </button>

                    {/* Chart Cards */}
                    {allCharts.map(chart => (
                        <div
                            key={chart.id}
                            onClick={() => onSelectChart(chart.id)}
                            className="group relative h-64 glass-panel rounded-2xl p-6 flex flex-col cursor-pointer hover:border-neon-gold/50 transition-all hover:shadow-[0_0_20px_rgba(245,158,11,0.1)]"
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />

                            <div className="relative z-10 flex-1">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-3 bg-neon-gold/10 rounded-xl text-neon-gold">
                                        <BarChart2 className="w-6 h-6" />
                                    </div>
                                    {/* Delete Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteChart(chart.id);
                                        }}
                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">{chart.title || "Untitled Chart"}</h3>
                                <p className="text-sm text-gray-400 mb-4">
                                    {chart.type ? chart.type.charAt(0).toUpperCase() + chart.type.slice(1) : 'Unknown'} Chart
                                </p>

                                <div className="flex flex-wrap gap-2 mt-auto">
                                    {chart.datasetIds?.slice(0, 2).map(dsId => {
                                        const ds = datasets.find(d => d.id === dsId);
                                        return ds ? (
                                            <span key={dsId} className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-gray-300 border border-white/10">
                                                {ds.name}
                                            </span>
                                        ) : null;
                                    })}
                                    {(chart.datasetIds?.length || 0) > 2 && (
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-gray-300 border border-white/10">
                                            +{(chart.datasetIds?.length || 0) - 2}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="relative z-10 mt-6 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-gray-500">
                                <span>{new Date(chart.createdAt).toLocaleDateString()}</span>
                                <span className="group-hover:text-neon-gold transition-colors">Edit Chart →</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-transparent text-gray-200">
            {/* Top Toolbar */}
            <header className="h-16 border-b border-white/10 glass-panel flex items-center justify-between px-6 flex-shrink-0 z-20 shadow-md">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => onSelectChart(null)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Back to Charts"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="h-6 w-px bg-white/10" />
                    <div className="flex items-center gap-2">
                        <BarChart2 className="w-6 h-6 text-neon-gold" />
                        <input
                            type="text"
                            value={chartConfig.title || ''}
                            onChange={(e) => setChartConfig(prev => ({ ...prev, title: e.target.value }))}
                            onFocus={(e) => e.target.select()}
                            placeholder="Untitled Chart"
                            className="bg-white/5 border border-white/20 hover:border-neon-gold/50 focus:border-neon-gold focus:bg-white/10 text-xl font-bold text-white tracking-tight focus:outline-none focus:ring-2 focus:ring-neon-gold/30 placeholder-gray-500 w-64 px-3 py-1 rounded-lg transition-all"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setChartConfig(defaultChartConfig)}
                        className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                        Reset
                    </button>
                    {isEditing && (
                        <button
                            onClick={() => onDeleteChart(chartToEdit.id)}
                            className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    )}
                    <div className="h-6 w-px bg-gray-700 mx-2" />
                    <button
                        onClick={handleSaveChart}
                        disabled={!isSaveValid || saveSuccess}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold shadow-[0_0_15px_rgba(245,158,11,0.2)] transition-all ${saveSuccess ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-neon-gold/10 text-neon-gold border border-neon-gold/50 hover:bg-neon-gold hover:text-white'} ${!isSaveValid ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                        title={!isSaveValid ? saveValidationErrors.join('\n') : 'Save Chart'}
                    >
                        {saveSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                        {saveSuccess ? 'Saved!' : 'Save Chart'}
                    </button>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden">
                {/* Left Sidebar: Data & Fields */}
                <div className="w-80 glass-panel border-r border-white/10 flex flex-col flex-shrink-0 z-10 overflow-y-auto custom-scrollbar">
                    <div className="p-5 space-y-4">
                        {/* Section 1: Data Source */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-neon-gold/70 uppercase tracking-widest flex items-center gap-2">
                                <Settings className="w-3.5 h-3.5" /> Data Source
                            </h3>
                            <div className="space-y-2">
                                {datasets.map(ds => {
                                    const isChecked = chartConfig.datasetIds?.includes(ds.id);
                                    const disabled = false;
                                    const matchingFields = selectedDatasets.length > 0 ? ds.schema.filter(c => selectedDatasets[0].schema.some(sc => sc.name === c.name)).map(c => c.name) : [];

                                    return (
                                        <label key={ds.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${isChecked ? 'bg-neon-gold/10 border-neon-gold/40' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                                            <div className="pt-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        const newIds = e.target.checked
                                                            ? [...(chartConfig.datasetIds || []), ds.id]
                                                            : (chartConfig.datasetIds || []).filter(id => id !== ds.id);
                                                        setChartConfig(prev => ({ ...prev, datasetIds: newIds }));
                                                    }}
                                                    disabled={disabled}
                                                    className="h-4 w-4 rounded border-white/20 bg-black/50 text-neon-gold focus:ring-neon-gold/50 disabled:opacity-50 cursor-pointer"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className={`text-sm font-medium truncate block ${isChecked ? 'text-neon-gold' : 'text-white/80 group-hover:text-gray-200'}`}>{ds.name}</span>
                                                {isChecked && selectedDatasets[0]?.id === ds.id && (
                                                    <span className="text-[10px] font-bold text-neon-gold/70 uppercase tracking-wide mt-0.5 block">Primary</span>
                                                )}
                                                {!isChecked && !disabled && matchingFields.length > 0 && (
                                                    <span className="text-[11px] text-emerald-400 flex items-center gap-1 mt-1 bg-emerald-900/20 px-1.5 py-0.5 rounded-md w-fit border border-emerald-500/20">
                                                        <span className="font-bold">Link</span> Connects via {matchingFields[0]}
                                                    </span>
                                                )}
                                            </div>
                                            {disabled && (
                                                <AlertTriangle className="h-4 w-4 text-amber-500/70 flex-shrink-0 mt-0.5" />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-px bg-gold-500/10 w-full" />

                        {/* Multi-Dataset Assistant */}
                        {selectedDatasets.length > 1 && (
                            <div className="space-y-3 p-3 rounded-xl bg-obsidian-800 border border-gold-500/10">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-bold text-gold-500/70 uppercase tracking-widest">Joined Fields</h4>
                                    <button
                                        onClick={() => setShowJoinedOnly(v => !v)}
                                        className={`text-xs px-2 py-1 rounded-lg border transition-colors ${showJoinedOnly ? 'bg-amber-500 text-black border-amber-400 font-bold' : 'bg-obsidian-900 text-gray-300 border-gold-500/20 hover:border-gold-500/40'}`}
                                        title="Filter field pickers to show only fields present across selected datasets"
                                    >
                                        {showJoinedOnly ? 'Showing Only Joined' : 'Show Only Joined'}
                                    </button>
                                </div>
                                {joinedFieldNames.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {joinedFieldNames.map(name => (
                                            <button
                                                key={name}
                                                onClick={() => {
                                                    const ds = selectedDatasets[0];
                                                    const v = `${ds.name}.${name}`;
                                                    setChartConfig(prev => ({ ...prev, xField: v, type: prev.type || 'bar' }));
                                                }}
                                                className="px-2 py-1 text-xs rounded-full bg-gold-500/10 text-gold-300 border border-gold-500/30 hover:bg-gold-500/20"
                                                title={`Use ${name} as dimension`}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-xs text-white/80">No common field names detected across selected datasets.</div>
                                )}
                            </div>
                        )}

                        {/* Heatmap Mapping Zone */}
                        {chartConfig.type === 'heatmap' && (
                            <>
                                <HeatmapMappingZone
                                    config={chartConfig}
                                    onUpdateConfig={(updates: Partial<ChartConfig>) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                    allCols={displayedCols}
                                    numericCols={numericCols}
                                />
                                <div className="h-px bg-gold-500/10 w-full" />
                            </>
                        )}

                        {/* Scientific Mapping Zone */}
                        {chartConfig.type === 'scientific' && (
                            <>
                                <ScientificMappingZone
                                    config={chartConfig}
                                    onUpdateConfig={(updates) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                    onUpdateScientificConfig={(updates) => setChartConfig(prev => ({
                                        ...prev,
                                        advanced: {
                                            ...prev.advanced,
                                            scientific: { ...prev.advanced?.scientific, ...updates }
                                        }
                                    }))}
                                    allCols={displayedCols.map(c => c.value)}
                                    numericCols={numericCols.map(c => c.value)}
                                    categoricalCols={displayedCols.filter(c => !c.isNumeric).map(c => c.value)}
                                />
                                <div className="h-px bg-gold-500/10 w-full" />
                            </>
                        )}

                        {/* Zone: KPI Card Mapping */}
                        {chartConfig.type === 'kpi-card' && (
                            <>
                                <KPIMappingZone
                                    config={chartConfig}
                                    onUpdateConfig={(updates: Partial<ChartConfig>) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                    allCols={displayedCols.map(c => c.value)}
                                    numericCols={numericCols.map(c => c.value)}
                                    categoricalCols={displayedCols.filter(c => !c.isNumeric).map(c => c.value)}
                                    dateCols={displayedCols.filter(c => c.value.toLowerCase().includes('date') || c.value.toLowerCase().includes('time')).map(c => c.value)}
                                />
                                <div className="h-px bg-gold-500/10 w-full" />
                            </>
                        )}

                        {/* Zone 1: Basic Mapping (Only for Standard Charts) */}
                        {!['scientific', 'globe', 'force-directed', 'map', '3d-bar', '3d', 'vector', 'heatmap', 'kpi-card'].includes(chartConfig.type || '') && (
                            <>
                                <BasicMappingZone
                                    config={chartConfig}
                                    onUpdateConfig={(updates: Partial<ChartConfig>) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                    allCols={displayedCols}
                                    numericCols={numericCols}
                                />

                                <div className="h-px bg-gold-500/10 w-full" />

                                {/* Zone 2: Encoding Bar (Icon Buttons) */}
                                <EncodingBar
                                    config={chartConfig}
                                    onUpdateConfig={(updates: Partial<ChartConfig>) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                    allCols={displayedCols}
                                />

                                <div className="h-px bg-gold-500/10 w-full" />
                            </>
                        )}

                        {/* Advanced Data Mapping (Moved from Right Sidebar) */}
                        {['globe', 'force-directed', 'map', '3d-bar', '3d', 'vector'].includes(chartConfig.type || '') && (
                            <>
                                <AdvancedDataMapping
                                    config={chartConfig}
                                    onChange={handleConfigChange}
                                    onAdvancedChange={(newAdvanced) => setChartConfig(prev => ({ ...prev, advanced: newAdvanced }))}
                                    allCols={allCols.map(c => c.value)}
                                    numericCols={numericCols.map(c => c.value)}
                                />
                                <div className="h-px bg-gold-500/10 w-full" />
                            </>
                        )}


                    </div>
                </div>


                {/* Center: Canvas & Bottom Panel */}
                <div className="flex-1 flex min-w-0 bg-transparent relative">
                    {/* Canvas Area */}
                    <div className="flex-1 p-6 overflow-auto flex flex-col items-center relative">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-neon-gold/5 via-transparent to-transparent pointer-events-none" />

                        <div className="w-full h-full max-w-7xl glass-panel rounded-2xl shadow-2xl shadow-black/80 border border-white/10 p-6 relative flex flex-col transition-all duration-300 z-10 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
                            {dataError ? (
                                <div className="flex items-center justify-center h-full text-zinc-500">
                                    <div className="text-center max-w-md">
                                        <div className="bg-red-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <AlertTriangle className="h-8 w-8 text-red-500" />
                                        </div>
                                        <h4 className="font-bold text-lg mb-2 text-white">Unable to Render Chart</h4>
                                        <p className="text-sm text-gray-500 leading-relaxed">{dataError}</p>
                                    </div>
                                </div>
                            ) : !isPreviewValid ? (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                    <div className="text-center max-w-md">
                                        <p className="mb-4">Configure your chart to see a preview.</p>
                                        {previewValidationErrors.length > 0 && (
                                            <div className="text-left bg-obsidian-800 p-4 rounded-lg border border-gold-500/20">
                                                <p className="text-xs font-bold text-gold-500 uppercase mb-2">Missing:</p>
                                                <ul className="text-sm text-white/80 space-y-1">
                                                    {previewValidationErrors.map(e => (
                                                        <li key={e} className="flex items-start gap-2">
                                                            <span className="text-gold-500 mt-0.5">•</span>
                                                            <span>{e}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div id="builder-preview" className="w-full h-full relative group flex flex-col">
                                    {/* Chart Container for Export */}
                                    <div ref={chartContainerRef} className="flex-1 min-h-0 relative w-full">
                                        <React.Suspense fallback={
                                            <div className="flex items-center justify-center h-full">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-gold"></div>
                                            </div>
                                        }>
                                            <ChartRenderer
                                                config={chartConfig}
                                                data={chartData}
                                                onSelectionChange={handleSelectionChange}
                                            />
                                        </React.Suspense>
                                    </div>

                                    {/* Export Button */}
                                    <div className="absolute top-2 right-2 z-50 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={handleExport}
                                            className="p-2 bg-obsidian-900/90 text-neon-gold rounded-lg border border-neon-gold/30 hover:bg-neon-gold/10 hover:border-neon-gold transition-all shadow-lg backdrop-blur-sm"
                                            title="Export Chart"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Bottom Bar: Horizontal Style Controls */}
                                    <HorizontalStyleBar config={chartConfig} onCustomizationChange={handleCustomizationChange} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Sidebar */}
                    <div className="w-80 border-l border-white/10 glass-panel flex flex-col">
                        <div className="p-4 border-b border-white/10">
                            <h3 className="text-xs font-bold text-neon-gold uppercase tracking-widest flex items-center gap-2">
                                <Settings className="w-3.5 h-3.5" /> Configuration
                            </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-6">
                            {/* Chart Type Picker */}
                            <div className="space-y-2">
                                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Visualization Type</h4>
                                <ChartTypePicker value={chartConfig.type as any} onSelect={(t) => {
                                    setChartConfig(prev => ({
                                        ...prev,
                                        type: t,
                                        advanced: (['scientific', 'vector', '3d', 'map', 'globe'].includes(t) && !prev.advanced) ? defaultAdvancedConfig : prev.advanced
                                    }));
                                }} />
                            </div>

                            {/* Zone 3: Chart Type Settings (Moved from Left) */}
                            <ChartTypeSettings
                                config={chartConfig}
                                onChange={handleConfigChange}
                                onAdvancedChange={handleAdvancedConfigChange}
                                onUpdateConfig={(updates: Partial<ChartConfig>) => setChartConfig(prev => ({ ...prev, ...updates }))}
                                allCols={allCols.map(c => c.value)}
                                numericCols={numericCols.map(c => c.value)}
                            />



                            <div className="pt-4 border-t border-white/10">
                                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3">Annotations</h4>
                                <AnnotationsEditor config={chartConfig} onChange={handleCustomizationChange} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper Components





const AnnotationsEditor: React.FC<{ config: any, onChange: any }> = ({ config, onChange }) => {
    const annotations = config.customization?.annotations || [];

    const addAnnotation = () => {
        const newAnn = {
            id: `ann-${Date.now()}`,
            text: 'New Text',
            position: 'top',
            style: {
                fontSize: 'medium',
                color: '#e5e7eb',
                isBold: false,
                isItalic: false
            }
        };
        const updated = [...annotations, newAnn];
        onChange({ target: { name: 'annotations', value: updated } });
    };

    const updateAnnotation = (id: string, updates: any) => {
        const updated = annotations.map((a: any) => a.id === id ? { ...a, ...updates } : a);
        onChange({ target: { name: 'annotations', value: updated } });
    };

    const removeAnnotation = (id: string) => {
        const updated = annotations.filter((a: any) => a.id !== id);
        onChange({ target: { name: 'annotations', value: updated } });
    };

    return (
        <div className="space-y-2">
            <button
                onClick={addAnnotation}
                className="w-full py-1.5 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-400 hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5 text-xs"
            >
                <span className="text-lg leading-none">+</span> Add Annotation
            </button>
            {annotations.map((ann: any) => (
                <div key={ann.id} className="p-3 rounded bg-white/5 border border-white/10 space-y-3">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={ann.text}
                            onChange={(e) => updateAnnotation(ann.id, { text: e.target.value })}
                            className="flex-1 bg-transparent border-none text-sm focus:ring-0 p-0 text-gray-200 placeholder-gray-600 font-medium"
                            placeholder="Annotation text"
                        />
                        <button onClick={() => removeAnnotation(ann.id)} className="text-red-400 hover:text-red-300 p-1">
                            <span className="sr-only">Remove</span>
                            ×
                        </button>
                    </div>

                    {/* Position Controls */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 uppercase font-bold">Pos</span>
                            <select
                                value={ann.position || 'top'}
                                onChange={(e) => updateAnnotation(ann.id, { position: e.target.value })}
                                className="bg-obsidian-800 border border-white/10 rounded text-xs text-gray-300 px-2 py-1 focus:ring-0 focus:border-gold-500"
                            >
                                <option value="top" className="bg-obsidian-800 text-gray-300">Top</option>
                                <option value="bottom" className="bg-obsidian-800 text-gray-300">Bottom</option>
                                <option value="overlay" className="bg-obsidian-800 text-gray-300">Overlay</option>
                            </select>
                        </div>

                        {ann.position === 'overlay' && (
                            <div className="flex items-center gap-1.5">
                                <div className="flex items-center gap-0.5 bg-black/20 rounded border border-white/10 px-1">
                                    <span className="text-[9px] text-gray-500 font-bold">X</span>
                                    <input
                                        type="number"
                                        value={ann.x ?? 50}
                                        onChange={(e) => updateAnnotation(ann.id, { x: Number(e.target.value) })}
                                        className="w-8 bg-transparent border-none text-xs text-white px-0.5 py-0.5 text-center focus:ring-0"
                                        min="0" max="100"
                                    />
                                    <span className="text-[9px] text-gray-500">%</span>
                                </div>
                                <div className="flex items-center gap-0.5 bg-black/20 rounded border border-white/10 px-1">
                                    <span className="text-[9px] text-gray-500 font-bold">Y</span>
                                    <input
                                        type="number"
                                        value={ann.y ?? 50}
                                        onChange={(e) => updateAnnotation(ann.id, { y: Number(e.target.value) })}
                                        className="w-8 bg-transparent border-none text-xs text-white px-0.5 py-0.5 text-center focus:ring-0"
                                        min="0" max="100"
                                    />
                                    <span className="text-[9px] text-gray-500">%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Style Controls */}
                    <div className="flex items-center gap-3">
                        {/* Font Size Selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 uppercase font-bold">Size</span>
                            <select
                                value={ann.style?.fontSize || 'medium'}
                                onChange={(e) => updateAnnotation(ann.id, { style: { ...ann.style, fontSize: e.target.value } })}
                                className="bg-obsidian-800 border border-white/10 rounded text-xs text-gray-300 px-2 py-1 focus:ring-0 focus:border-gold-500 w-24"
                            >
                                <option value="small" className="bg-obsidian-800 text-gray-300">Small</option>
                                <option value="medium" className="bg-obsidian-800 text-gray-300">Medium</option>
                                <option value="large" className="bg-obsidian-800 text-gray-300">Large</option>
                                <option value="huge" className="bg-obsidian-800 text-gray-300">Huge</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => updateAnnotation(ann.id, { style: { ...ann.style, isBold: !ann.style.isBold } })}
                                className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded border transition-colors ${ann.style.isBold ? 'bg-white/20 border-white/30 text-white' : 'border-white/10 text-gray-400 hover:bg-white/10'}`}
                                title="Bold"
                            >
                                B
                            </button>
                            <button
                                onClick={() => updateAnnotation(ann.id, { style: { ...ann.style, isItalic: !ann.style.isItalic } })}
                                className={`w-7 h-7 flex items-center justify-center text-xs italic rounded border transition-colors ${ann.style.isItalic ? 'bg-white/20 border-white/30 text-white' : 'border-white/10 text-gray-400 hover:bg-white/10'}`}
                                title="Italic"
                            >
                                I
                            </button>
                        </div>

                        <div className="flex-1" />
                        <div className="relative group">
                            <input
                                type="color"
                                value={ann.style.color}
                                onChange={(e) => updateAnnotation(ann.id, { style: { ...ann.style, color: e.target.value } })}
                                className="w-7 h-7 rounded overflow-hidden border-none p-0 cursor-pointer opacity-0 absolute inset-0 z-10"
                            />
                            <div className="w-7 h-7 rounded border border-white/20 flex items-center justify-center" style={{ backgroundColor: ann.style.color }}>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ChartBuilder;
